import * as Popover from '@radix-ui/react-popover';
import { createContext, useContext, useState, type ReactNode } from 'react';
import { Link, useInRouterContext } from 'react-router';
import { searchUrl, useSearchSurface } from '@/features/search/api';
import { ChevronRight, Funnel, Search, X } from 'lucide-react';
import { cx } from '@/lib/cx';
import { useIsMobile } from '@/lib/hooks';
import { Button } from './Button';
import { Drawer } from './Drawer';
import { Input } from './Field';
import { Spinner } from './Spinner';

/* ---------------------------------------------------------------- Breadcrumbs */

export interface Crumb {
  label: string;
  to?: string;
}

export function Breadcrumbs({ items }: { items: Crumb[] }) {
  return (
    <nav aria-label="Breadcrumb" className="breadcrumbs">
      <ol>
        {items.map((c, i) => {
          const last = i === items.length - 1;
          return (
            <li key={`${c.label}-${i}`}>
              {c.to && !last ? <Link to={c.to}>{c.label}</Link> : <span aria-current={last ? 'page' : undefined}>{c.label}</span>}
              {!last && <ChevronRight size={12} aria-hidden className="crumb-sep" />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/* ---------------------------------------------------------------- PageHeader */

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  breadcrumbs?: Crumb[];
  actions?: ReactNode;
  /** Status pills and small facts under the title. */
  meta?: ReactNode;
  leading?: ReactNode;
  className?: string;
}

export function PageHeader({ title, description, breadcrumbs, actions, meta, leading, className }: PageHeaderProps) {
  return (
    <header className={cx('page-header', className)}>
      {breadcrumbs && <Breadcrumbs items={breadcrumbs} />}
      <div className="page-header-main">
        {leading && <div className="page-header-leading">{leading}</div>}
        <div className="page-header-text">
          <h1 className="page-title">{title}</h1>
          {description && <p className="page-desc">{description}</p>}
          {meta && <div className="page-meta">{meta}</div>}
        </div>
        {actions && <div className="page-actions">{actions}</div>}
      </div>
    </header>
  );
}

/* ---------------------------------------------------------------- Toolbar / FilterBar */

export function Toolbar({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('toolbar', className)} role="toolbar">{children}</div>;
}

export function CollectionHeader({ title, description, actions, children }: { title: string; description?: string; actions?: ReactNode; children?: ReactNode }) {
  return <header className="collection-header">
    <div className="collection-heading"><div><h2>{title}</h2>{description && <p>{description}</p>}</div>{actions && <div className="collection-actions">{actions}</div>}</div>
    {children && <div className="collection-tabs">{children}</div>}
  </header>;
}

export const CompactFiltersContext = createContext(true);

export interface FilterBarProps {
  search?: { value: string; onChange: (v: string) => void; placeholder?: string; label?: string };
  /** Filter controls. Inline on desktop, inside a bottom sheet on mobile. */
  children?: ReactNode;
  activeCount?: number;
  onClear?: () => void;
  trailing?: ReactNode;
  fetching?: boolean;
  resultSummary?: ReactNode;
}

export function FilterBar({ search, children, activeCount = 0, onClear, trailing, fetching, resultSummary }: FilterBarProps) {
  const mobile = useIsMobile();
  const compact = useContext(CompactFiltersContext);
  const inRouter = useInRouterContext();
  const [open, setOpen] = useState(false);
  return (
    <div className="filterbar">
      <div className="filterbar-row">
        {search && (
          <Input
            type="search"
            className="filterbar-search"
            aria-label={search.label ?? 'Search'}
            placeholder={search.placeholder ?? 'Search'}
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            leading={<Search size={15} />}
            trailing={
              search.value ? (
                <button type="button" className="input-clear" aria-label="Clear search" onClick={() => search.onChange('')}>
                  <X size={14} />
                </button>
              ) : fetching ? (
                <Spinner size={13} />
              ) : undefined
            }
          />
        )}
        {children && mobile && (
          <Button icon={<Funnel size={15} />} onClick={() => setOpen(true)} aria-haspopup="dialog">
            Filters{activeCount ? ` · ${activeCount}` : ''}
          </Button>
        )}
        {children && !mobile && (compact ? <Popover.Root open={open} onOpenChange={setOpen}>
          <Popover.Trigger asChild><Button icon={<Funnel size={15} />}>Filters{activeCount ? ` (${activeCount})` : ''}</Button></Popover.Trigger>
          <Popover.Portal><Popover.Content className="pf-filter-popover" align="start" sideOffset={8} aria-label="Filters">
            <div className="pf-filter-heading"><strong>Filters</strong><Popover.Close asChild><Button size="sm" variant="ghost" aria-label="Close filters">Done</Button></Popover.Close></div>
            <div className="filterbar-sheet">{children}</div>
            {onClear && <Button variant="ghost" size="sm" onClick={onClear} disabled={!activeCount}>Clear all</Button>}
          </Popover.Content></Popover.Portal>
        </Popover.Root> : <div className="filterbar-filters">{children}</div>)}
        <div className="spacer" />
        {!mobile && activeCount > 0 && onClear && (
          <Button variant="ghost" size="sm" onClick={onClear} icon={<X size={14} />}>
            Clear filters
          </Button>
        )}
        {trailing}
      </div>
      {inRouter && search?.value.trim() && <SearchEverythingLink query={search.value} />}
      {resultSummary && (
        <div className="filterbar-summary" aria-live="polite">
          {resultSummary}
        </div>
      )}
      {children && mobile && (
        <Drawer
          open={open}
          onOpenChange={setOpen}
          title="Filters"
          footer={
            <>
              {onClear && (
                <Button variant="ghost" onClick={onClear} disabled={!activeCount}>
                  Clear all
                </Button>
              )}
              <Button variant="primary" onClick={() => setOpen(false)}>
                Show results
              </Button>
            </>
          }
        >
          <div className="filterbar-sheet">{children}</div>
        </Drawer>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- LoadMore (cursor pagination) */

export function LoadMore({ hasMore, loading, onLoadMore, shown, noun = 'items' }: { hasMore: boolean; loading: boolean; onLoadMore: () => void; shown: number; noun?: string }) {
  if (!shown) return null;
  return (
    <div className="load-more">
      <span className="load-more-count tabular">
        Showing {shown} {noun}
        {hasMore ? '' : ' · end of list'}
      </span>
      {hasMore && (
        <Button size="sm" onClick={onLoadMore} loading={loading} loadingText="Loading…">
          Load more
        </Button>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- Timeline */

export interface TimelineItem {
  id: string;
  icon?: ReactNode;
  title: ReactNode;
  meta?: ReactNode;
  body?: ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'info';
  badge?: ReactNode;
}

export function Timeline({ items, label }: { items: TimelineItem[]; label: string }) {
  return (
    <ol className="timeline" aria-label={label}>
      {items.map((it) => (
        <li key={it.id} className={cx('timeline-item', `tone-${it.tone ?? 'neutral'}`)}>
          <span className="timeline-dot" aria-hidden>
            {it.icon}
          </span>
          <div className="timeline-content">
            <div className="timeline-title">
              {it.title}
              {it.badge}
            </div>
            {it.meta && <div className="timeline-meta">{it.meta}</div>}
            {it.body && <div className="timeline-body">{it.body}</div>}
          </div>
        </li>
      ))}
    </ol>
  );
}

function SearchEverythingLink({ query }: { query: string }) {
  const path = useSearchSurface();
  return <Link className="filterbar-summary" to={searchUrl(path, query)}>Search everything for this</Link>;
}
